import React from "react";
import { getGtmContainerIds, isIndexingAllowed, TRACKING_CONFIGS } from "@constant/tracking";
import { DEFAULT_DESCRIPTION_METATAG, DEFAULT_IMAGE_METATAG, DEFAULT_KEYWORD_METATAG, DEFAULT_SITENAME_METATAG, DEFAULT_TITLE_METATAG } from "@constant/defaultValues";
import Head from "next/head";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

// Transactional/private pages stay noindex even for tenants with allowIndexing
const PRIVATE_PAGE_SEGMENTS = [
  'cart', 'checkout', 'orderconfirmation', 'myorders', 'appointmenthistory',
  'profile', 'feedback', 'invoice', 'advancerefund'
];

/**
 * GTM <noscript> fallback iframes. GTM requires this in <body> (not <head>),
 * so it is rendered from _app.tsx at the top of the page tree.
 */
export function GtmNoScript({ storeData }) {
  const gtmIds = getGtmContainerIds(storeData?.tenantId, storeData?.storeId);
  if (!gtmIds.length) return null;

  return (
    <>
      {gtmIds.map((id) => (
        <noscript
          key={`gtm-noscript-${id}`}
          dangerouslySetInnerHTML={{
            __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
          }}
        />
      ))}
    </>
  );
}

export default function HeadMetaTags({ title, description, image, siteName, storeData }) {

  const { asPath } = useRouter();
  // Some templates (servicePdp, staffPdp) render this component without storeData; fall back to Redux
  const reduxStoreData = useSelector((state: any) => state?.store?.storeData);
  if (!storeData) storeData = reduxStoreData || {};

  const isPrivatePage = PRIVATE_PAGE_SEGMENTS.some(segment => asPath?.toLowerCase().includes(segment));
  const allowIndexing = !isPrivatePage && isIndexingAllowed(storeData?.tenantId, storeData?.storeId);
  const robotsTagData = allowIndexing ? "index, follow" : "noindex, nofollow";

  const titleTagData = title || DEFAULT_TITLE_METATAG;
  const descriptionTagData = description || DEFAULT_DESCRIPTION_METATAG;
  const imageTagData = image || DEFAULT_IMAGE_METATAG;
  const siteNameTagData = siteName || DEFAULT_SITENAME_METATAG;
  const keywordsTagData = descriptionTagData + ' ' + DEFAULT_KEYWORD_METATAG;

  return (
    <Head>
      {/* Coomon meta tags */}
      <title>{titleTagData}</title>
      <meta name="title" key="title" content={titleTagData} />
      <meta name="description" key="description" content={descriptionTagData} />
      <meta name="keywords" key="keywords" content={keywordsTagData} />
      <meta name="robots" key="robots" content={robotsTagData} />
      <meta name="application-name" content="Respark" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Respark" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* FB meta Tags */}
      {/* <meta property="og:url" content='www.devourin.com' key="ogurl" /> */}
      <meta property="og:site_name" content={siteNameTagData} key="ogsitename" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={titleTagData} key="ogtitle" />
      <meta property="og:description" content={descriptionTagData} key="ogdesc" />
      <meta property="og:image" content={imageTagData} key="ogimage" />

      {/* Twitter meta Tags */}
      <meta name="twitter:title" content={titleTagData} key="twittertitle" />
      <meta name="twitter:description" content={descriptionTagData} key="twitterdesc" />
      <meta name="twitter:image" content={imageTagData} key="twitterimage" />
      <meta name="twitter:site" content="Devourin Salon" key="twittersitename" />
      <meta name="twitter:creator" content="Devourin" />
      {/* application meta tags */}

      {/* <meta name="viewport" content="width=device-width,initial-scale=1" /> */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
      <link rel="shortcut icon" type="image/png" href="/favicon.ico" />

      {/* Dynamic Tracking Scripts */}
      {TRACKING_CONFIGS.map((config, index) => {
        const tenantMatches = Number(storeData?.tenantId) === config.tenantId;
        const storeMatches = !config.storeId || Number(storeData?.storeId) === config.storeId;

        if (!tenantMatches || !storeMatches) return null;

        return (
          <React.Fragment key={`tracking-${config.tenantId}-${index}`}>
            {config.tracking.map((tracking, trackingIndex) => {
              switch (tracking.type) {
                case 'gtm':
                  // GTM container snippet; the matching <body> noscript iframe is rendered by GtmNoScript in _app.tsx
                  return (
                    <script
                      key={`gtm-${tracking.id}`}
                      dangerouslySetInnerHTML={{
                        __html: `
                          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                          })(window,document,'script','dataLayer','${tracking.id}');`
                      }}
                    />
                  );

                case 'google-tag':
                case 'google-analytics':
                  const scriptId = `gtag-${tracking.type}-${tracking.id}`;
                  return (
                    <script
                      key={scriptId}
                      dangerouslySetInnerHTML={{
                        __html: `
                          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                          'https://www.googletagmanager.com/gtag/js?id='+i+dl;f.parentNode.insertBefore(j,f);
                          })(window,document,'script','dataLayer','${tracking.id}');
                          window.dataLayer = window.dataLayer || [];
                          function gtag(){dataLayer.push(arguments);}
                          gtag('js', new Date());
                          gtag('config', '${tracking.id}', {
                            send_page_view: false
                          });`
                      }}
                    />
                  );

                case 'google-ads':
                  return (
                    <script
                      key={`tracking-${trackingIndex}`}
                      dangerouslySetInnerHTML={{
                        __html: `
                          if (typeof gtag !== 'undefined') {
                            gtag('config', '${tracking.id}');
                          }`
                      }}
                    />
                  );

                case 'amp-analytics':
                  return (
                    <React.Fragment key={`tracking-${trackingIndex}`}>
                      <script
                        async
                        custom-element="amp-analytics"
                        src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
                      />
                      <script
                        dangerouslySetInnerHTML={{
                          __html: `
                            <amp-analytics type="gtag" data-credentials="include">
                              <script type="application/json">
                                ${JSON.stringify({
                            vars: {
                              gtag_id: "${config.tracking.find(t => t.type === 'google-tag' || t.type === 'google-ads')?.id}",
                              config: {
                                "${config.tracking.find(t => t.type === 'google-tag' || t.type === 'google-ads')?.id}": { groups: "default" }
                              }
                            },
                            triggers: {}
                          })}
                              </script>
                            </amp-analytics>`
                        }}
                      />
                    </React.Fragment>
                  );

                case 'facebook-pixel':
                  return (
                    <React.Fragment key={`tracking-${trackingIndex}`}>
                      <script
                        dangerouslySetInnerHTML={{
                          __html: `
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', '${tracking.id}');
                            fbq('track', 'PageView');`
                        }}
                      />
                      <noscript
                        dangerouslySetInnerHTML={{
                          __html: `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${tracking.id}&ev=PageView&noscript=1" />`
                        }}
                      />
                    </React.Fragment>
                  );

                default:
                  return null;
              }
            })}
          </React.Fragment>
        );
      })}
    </Head>
  );
}
